import React, { FC, Suspense, useState } from 'react';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { Drawer } from 'src/components/widgets/drawer';
import { T, backofficeUrl } from 'src/shared/helpers';
import {
    EmptyState,
    FooterButton,
    ItemActionSuccess,
    ItemDot,
    ItemName,
    ItemNameAction,
    PanelItem,
    PanelItemSpaced,
    StackedBody,
    StackedFooterButtons
} from '../common/styled';
import type { addQuoteRulePanel_AssignProductMutation } from './__generated__/addQuoteRulePanel_AssignProductMutation.graphql';
import type { addQuoteRulePanel_QuoteRulesListQuery } from './__generated__/addQuoteRulePanel_QuoteRulesListQuery.graphql';
import { PanelLinesSkeleton } from '../common/skeletons';

const AddQuoteRulePanelContent: FC<{
    productId: number;
    productRelayId: string;
    existingRuleId: string | null;
    onBack: () => void;
    onAssigned: () => void;
    onCreateNew?: () => void;
}> = ({ productId, productRelayId, existingRuleId, onBack, onAssigned, onCreateNew }) => {
    const navigate = useNavigate();
    const [assigning, setAssigning] = useState<string | null>(null);

    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query addQuoteRulePanel_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productRelayId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<addQuoteRulePanel_QuoteRulesListQuery>(
        graphql`
            query addQuoteRulePanel_QuoteRulesListQuery {
                quoteRulesList {
                    id
                    quoteRuleID
                    name
                }
            }
        `,
        {}
    );

    const allRules = React.useMemo(() => {
        const rules = data?.quoteRulesList || [];
        return rules.map((rule) => ({ id: rule.id, name: rule.name || `#${rule.quoteRuleID}` }));
    }, [data]);

    const [commitAssign] = useMutation<addQuoteRulePanel_AssignProductMutation>(graphql`
        mutation addQuoteRulePanel_AssignProductMutation($ruleId: ID!, $productIds: [Long!]!) {
            assignProductsToQuoteRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const handleAssign = (rule: { id: string; name: string }) => {
        setAssigning(rule.id);
        commitAssign({
            variables: {
                ruleId: rule.id,
                productIds: [productId]
            },
            updater: (store) => {
                store.invalidateStore();
            },
            onCompleted: () => {
                setAssigning(null);
                onAssigned();
            },
            onError: () => {
                setAssigning(null);
            }
        });
    };

    const availableRules = allRules.filter((rule) => rule.id !== existingRuleId);

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: T._('Add get a quote rule', 'Admin'), subtitle: productName }}
            width={580}
            footer={
                <StackedFooterButtons>
                    <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                </StackedFooterButtons>
            }
        >
            <StackedBody>
                {availableRules.length === 0 && <EmptyState>{T._('All quote rules are already assigned', 'Admin')}</EmptyState>}
                {availableRules.map((rule) => (
                    <PanelItem key={rule.id} $clickable={assigning === null} onClick={() => assigning === null && handleAssign(rule)}>
                        <ItemDot $color='#3b82f6' />
                        <ItemName>{rule.name}</ItemName>
                        <ItemActionSuccess>{assigning === rule.id ? T._('Assigning...', 'Admin') : T._('Assign', 'Admin')}</ItemActionSuccess>
                    </PanelItem>
                ))}
                <PanelItemSpaced
                    $clickable
                    onClick={() => {
                        if (onCreateNew) {
                            onCreateNew();
                        } else {
                            onBack();
                            navigate(backofficeUrl('/tools/get-a-quote/quote-rule/?fromProductId=' + productId));
                        }
                    }}
                >
                    <ItemDot $color='#d1d5db' />
                    <ItemNameAction>
                        <Icon>
                            <PlusIcon />
                        </Icon>
                        {T._('Create new', 'Admin')}
                    </ItemNameAction>
                </PanelItemSpaced>
            </StackedBody>
        </Drawer>
    );
};

export const AddQuoteRulePanel: FC<{
    productId: number;
    productRelayId: string;
    existingRuleId: string | null;
    onBack: () => void;
    onAssigned: () => void;
    onCreateNew?: () => void;
}> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer
                    isOpen={true}
                    isStacked
                    onClose={props.onBack}
                    header={{ title: T._('Add get a quote rule', 'Admin'), subtitle: '' }}
                    width={580}
                    footer={
                        <StackedFooterButtons>
                            <FooterButton onClick={props.onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                        </StackedFooterButtons>
                    }
                >
                    <StackedBody>
                        <PanelLinesSkeleton />
                    </StackedBody>
                </Drawer>
            }
        >
            <AddQuoteRulePanelContent {...props} />
        </Suspense>
    );
};
